import classNames from 'classnames';
import React, { cloneElement, Component, createRef } from 'react';

import { toggleTextSelection } from '@sbu/utils.dom';

import { SelectionContext, SelectionContextProps } from './SelectionContext';
import css from './SelectionItem.module.scss';

export const DATA_ATTRIBUTE_INDEX = 'selection_index';

interface SelectionItemProps<T = any> {
  model: T | T[];
  highlight?: boolean;
  disabled?: boolean;
}

export class SelectionItem<T = any> extends Component<
  React.PropsWithChildren<SelectionItemProps<T>>
> {
  context: any;
  static contextType = SelectionContext;
  static defaultProps: Partial<SelectionItemProps> = {
    highlight: false,
  };

  elementRef = createRef<HTMLDivElement>();
  modifier?: 'shift' | 'meta';

  componentDidMount() {
    this.context.register(this);
  }

  componentWillUnmount() {
    this.context.unregister(this);
  }

  onKeyDown(event: React.KeyboardEvent) {
    // Space is not recognized by `key` prop
    if (event.key === 'Enter' || event.keyCode === 32) {
      event.preventDefault();
      this.context.toggle(this);
    } else if (event.key === 'Shift') {
      toggleTextSelection(false);
    }
  }

  setModifiers(event: React.MouseEvent) {
    if (event.metaKey || event.ctrlKey) {
      this.modifier = 'meta';
    } else if (event.shiftKey) {
      this.modifier = 'shift';
    }

    // Clear the modifier if we don't use it
    setTimeout(() => (this.modifier = undefined), 500);
  }

  toggle() {
    if (!this.props.disabled) {
      this.context.toggle(this);
    }
  }

  renderChild = (context: SelectionContextProps) => {
    const { children, disabled, model, highlight } = this.props;
    const selected = context.selections.some((s) => s === model);

    return React.Children.map(children, (child: any) =>
      cloneElement(child, {
        ...child.props,
        tabIndex: -1,
        checked: selected,
        disabled,
        className: classNames(child.props.className, css.selectionItem, {
          [css.selected]: selected && highlight,
          [css.disabled]: disabled,
        }),
        onChange: (event) => {
          this.toggle();
          if (child.props.onChange) {
            child.props.onChange(event);
          }
        },
        onBlur: (event) => {
          setTimeout(() => toggleTextSelection(true), 200);
          if (child.props.onBlur) {
            child.props.onBlur(event);
          }
        },
        onClick: (event: React.MouseEvent) => {
          // Checkboxes are handled `onChange`
          const inputElement = event.nativeEvent.target as HTMLInputElement;
          if (inputElement.type !== 'checkbox') {
            this.toggle();
          }

          if (child.props.onClick) {
            child.props.onClick(event);
          }
        },
        onMouseDown: (event: React.MouseEvent) => {
          this.setModifiers(event);
          if (child.props.onMouseDown) {
            child.props.onMouseDown(event);
          }
        },
        onKeyDown: (event: React.KeyboardEvent) => {
          this.onKeyDown(event);
          if (child.props.onKeyDown) {
            child.props.onKeyDown(event);
          }
        },
      })
    );
  };

  render() {
    const props = { [DATA_ATTRIBUTE_INDEX]: 'true' };
    return (
      <div ref={this.elementRef} {...props}>
        <SelectionContext.Consumer>
          {this.renderChild}
        </SelectionContext.Consumer>
      </div>
    );
  }
}
